// src/pages/DataRetention.jsx
// 年度の締め・保管（保存期間）【2026-09-17】
//
//   ① 学期中          … ふつうに使う
//   ② 締める          … その年度（学期）の名簿・出席を読み取り専用にする
//   ③ 暗号化して書き出す → 消去 … 公開鍵で暗号化したファイルにしてから、データベースから消す
//                          （名前を含まない集計だけ残す）
//   ④ 廃棄            … 保存期間が過ぎた暗号化ファイルを消す
//
//   締める・再開・消去・廃棄は 2段階で確認する。
//     1段目: 何が対象かを見て「内容を確認した」にチェック
//     2段階目: 決まった文言（例「2025年度前期を締める」）を入力してから実行
//   復号の秘密鍵はサーバーに置かない。このブラウザで作った秘密鍵はダウンロードだけする。
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const STATUS = {
  closed:   { label: '締め済み（読み取り専用）', color: '#2563eb', bg: '#eff6ff' },
  exported: { label: '暗号化して書き出し済み',   color: '#7c3aed', bg: '#f5f3ff' },
  purged:   { label: 'データベースから消去済み', color: '#6b7280', bg: '#f3f4f6' },
  reopened: { label: '再開済み',                 color: '#b45309', bg: '#fffbeb' },
};
const ACTION = {
  close:   { title: '締める',                 button: '締める',           danger: false },
  reopen:  { title: '締めを再開する',         button: '再開する',         danger: false },
  purge:   { title: 'データベースから消去する', button: '消去する',         danger: true },
  destroy: { title: '暗号化ファイルを廃棄する', button: '廃棄する',         danger: true },
};

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' });

async function api(path, opts = {}) {
  const r = await fetch('/api/retention' + path, { ...opts, headers: headers() });
  let d = {};
  try { d = await r.json(); } catch (e) { d = { success: false, message: `サーバーエラー（${r.status}）` }; }
  if (!r.ok || d.success === false) {
    const e = new Error(d.message || `エラー（${r.status}）`);
    e.code = d.code;
    throw e;
  }
  return d;
}

const day = (v) => (v ? String(v).slice(0, 10) : '—');
const today = () => new Date().toISOString().slice(0, 10);
const fmtBytes = (n) => (n == null ? '' : n < 1024 * 1024 ? `${Math.ceil(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

// ── 鍵をブラウザで作る ────────────────────────────────────────────
function toPem(buf, label) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const b64 = window.btoa(bin);
  const lines = b64.match(/.{1,64}/g).join('\n');
  return `-----BEGIN ${label}-----\n${lines}\n-----END ${label}-----\n`;
}

async function generateKeyPair() {
  const pair = await window.crypto.subtle.generateKey(
    { name: 'RSA-OAEP', modulusLength: 4096, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
    true, ['encrypt', 'decrypt']);
  const pub = await window.crypto.subtle.exportKey('spki', pair.publicKey);
  const priv = await window.crypto.subtle.exportKey('pkcs8', pair.privateKey);
  return { publicPem: toPem(pub, 'PUBLIC KEY'), privatePem: toPem(priv, 'PRIVATE KEY') };
}

function saveText(text, name) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/x-pem-file' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── 2段階の確認 ──────────────────────────────────────────────────
export function ConfirmSteps({ action, plan, busy, error, onRun, onCancel, onReload }) {
  const [step, setStep] = useState(1);
  const [checked, setChecked] = useState(false);
  const [phrase, setPhrase] = useState('');
  const meta = ACTION[action];
  const blocked = (plan.blockers || []).length > 0;
  const matches = phrase.trim() === plan.phrase;
  const counts = plan.counts || {};
  const classes = plan.classes || [];
  const stale = error && /開き直して/.test(error);

  return (
    <div role="dialog" aria-modal="true" aria-label={meta.title}
         onClick={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}
         style={{ position: 'fixed', inset: 0, zIndex: 4300, background: 'rgba(0,0,0,0.35)',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  padding: '6vh 16px', overflowY: 'auto' }}>
      <div className="card" style={{ width: 'min(620px, 100%)', margin: 0 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{plan.phrase}</h2>
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>確認 {step} / 2</span>
        </div>

        {step === 1 && (
          <div data-step="1">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem', margin: '0.5rem 0 0.75rem' }}>
              <Stat label="授業" value={classes.length || plan.classCount || 0} />
              <Stat label="学生" value={counts.students || 0} />
              <Stat label="出席の記録" value={counts.attendance || 0} />
              <Stat label="回" value={counts.sessions || 0} />
            </div>
            {action === 'close' && (
              <p style={{ fontSize: '0.9rem', margin: '0 0 0.5rem' }}>
                締めると、この授業の名簿・出席・回は<b>追加・削除・変更できなくなります</b>（見る・出力するのはできます）。
                あとから「再開」で元に戻せます。
                {plan.purgeAfterMonths != null && <> 設定では {plan.purgeAfterMonths}か月たったら消去の時期として知らせます。</>}
              </p>
            )}
            {action === 'reopen' && <p style={{ fontSize: '0.9rem' }}>再開すると、また名簿・出席を変更できるようになります。</p>}
            {action === 'purge' && (
              <p style={{ fontSize: '0.9rem', color: '#991b1b' }}>
                授業・名簿・出席・成績をデータベースから<b>消します。元に戻せません</b>。
                名前を含まない集計（授業名・人数・平均出席率）だけ残ります。
                あとで見るには、書き出した暗号化ファイルと秘密鍵が必要です。
              </p>
            )}
            {action === 'destroy' && (
              <p style={{ fontSize: '0.9rem', color: '#991b1b' }}>
                サーバーにある暗号化ファイルを<b>消します。元に戻せません</b>。
                手元にダウンロードした分は消えないので、そちらも合わせて処分してください。
              </p>
            )}
            {classes.length > 0 && (
              <details style={{ margin: '0.25rem 0 0.75rem' }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.9rem' }}>対象の授業 {classes.length}件</summary>
                <ul style={{ maxHeight: 180, overflowY: 'auto', margin: '0.4rem 0', paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
                  {classes.map(c => <li key={c.id}>{c.name}</li>)}
                </ul>
              </details>
            )}
            {(plan.warnings || []).map((w, i) => (
              <div key={'w' + i} style={box('#fffbeb', '#b45309')}>⚠ {w}</div>
            ))}
            {(plan.blockers || []).map((b, i) => (
              <div key={'b' + i} style={box('#fef2f2', '#b91c1c')} data-blocker>✕ {b}</div>
            ))}
            {!blocked && (
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.75rem 0', fontWeight: 600 }}>
                <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} />
                内容を確認しました
              </label>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button className="btn-secondary" onClick={onCancel}>やめる</button>
              {!blocked && (
                <button className="btn-primary" disabled={!checked} onClick={() => setStep(2)}>次へ（最後の確認）</button>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-step="2">
            <p style={{ fontSize: '0.9rem' }}>
              間違い防止のため、次の文言をそのまま入力してください。
            </p>
            <div style={{ fontFamily: 'monospace', fontSize: '1.05rem', padding: '0.5rem 0.75rem',
                          background: '#f3f4f6', borderRadius: 6, userSelect: 'all', marginBottom: '0.5rem' }}>
              {plan.phrase}
            </div>
            <input type="text" value={phrase} autoFocus aria-label="確認の文言"
                   onChange={e => setPhrase(e.target.value)}
                   onPaste={e => e.preventDefault()}
                   placeholder={plan.phrase}
                   style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', boxSizing: 'border-box',
                            border: `2px solid ${phrase && !matches ? '#fca5a5' : matches ? '#86efac' : '#d1d5db'}`, borderRadius: 6 }} />
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.25rem' }}>
              貼り付けはできません。確認画面を開いてから15分以内に実行してください。
            </div>
            {error && <div style={box('#fef2f2', '#b91c1c')}>{error}</div>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <button className="btn-secondary" disabled={busy} onClick={() => { setStep(1); setPhrase(''); }}>戻る</button>
              {stale && <button className="btn-secondary" disabled={busy} onClick={onReload}>確認画面を開き直す</button>}
              <button className={meta.danger ? 'btn-danger' : 'btn-primary'}
                      disabled={!matches || busy}
                      onClick={() => onRun(phrase.trim())}>
                {busy ? '実行中…' : meta.button}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: '#f9fafb', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{Number(value).toLocaleString()}</div>
    </div>
  );
}

const box = (bg, color) => ({ background: bg, color, borderRadius: 6, padding: '0.45rem 0.7rem', margin: '0.35rem 0', fontSize: '0.88rem' });

function Badge({ status }) {
  const s = STATUS[status] || { label: status, color: '#374151', bg: '#f3f4f6' };
  return (
    <span style={{ fontSize: '0.78rem', color: s.color, background: s.bg, borderRadius: 999,
                   padding: '0.1rem 0.55rem', whiteSpace: 'nowrap', border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  );
}

// ── 設定 ─────────────────────────────────────────────────────────
function SettingsCard({ settings, onSaved, setMsg, setErr }) {
  const [months, setMonths] = useState(6);
  const [years, setYears] = useState(5);
  const [pem, setPem] = useState('');
  const [editKey, setEditKey] = useState(false);
  const [generated, setGenerated] = useState(null);   // { privatePem, downloaded }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setMonths(settings.readOnlyMonths);
    setYears(settings.keepYears);
    setPem(settings.publicKeyPem || '');
    setEditKey(!settings.publicKeyPem);
  }, [settings]);

  const gen = async () => {
    setErr('');
    try {
      if (!window.crypto || !window.crypto.subtle) throw new Error('このブラウザでは鍵を作れません（https で開いてください）');
      setBusy(true);
      const k = await generateKeyPair();
      setPem(k.publicPem);
      setGenerated({ privatePem: k.privatePem, downloaded: false });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const downloadPrivate = () => {
    saveText(generated.privatePem, `nekotam-archive-private-key-${today()}.pem`);
    setGenerated(g => ({ ...g, downloaded: true }));
  };

  const keyChanged = (pem || '').trim() !== (settings?.publicKeyPem || '').trim();
  const mustDownload = generated && !generated.downloaded && keyChanged;

  const save = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const body = { readOnlyMonths: Number(months), keepYears: Number(years) };
      if (keyChanged) body.publicKeyPem = pem;
      const d = await api('/settings', { method: 'PUT', body: JSON.stringify(body) });
      setMsg(d.message);
      setGenerated(null);
      onSaved(d.settings);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="card">
      <div className="card-header"><h2>保存期間の設定</h2></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem' }}>
        <label className="form-row" style={{ marginBottom: 0 }}>
          <span>締めてからデータベースに残す期間</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <input type="number" min="0" max="120" value={months} aria-label="読み取り専用で残す月数"
                   onChange={e => setMonths(e.target.value)} style={{ width: 90 }} /> か月
          </span>
          <small style={{ color: '#6b7280' }}>過ぎると「消去の時期」と表示します（自動では消しません）</small>
        </label>
        <label className="form-row" style={{ marginBottom: 0 }}>
          <span>暗号化ファイルの保存期間</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <input type="number" min="0" max="50" value={years} aria-label="暗号化ファイルの保存年数"
                   onChange={e => setYears(e.target.value)} style={{ width: 90 }} /> 年
          </span>
          <small style={{ color: '#6b7280' }}>書き出した日から数えます。過ぎると「廃棄の時期」と表示します</small>
        </label>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>暗号化用の鍵</div>
        {settings?.publicKeyFingerprint && !editKey ? (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={box('#f0fdf4', '#166534')}>登録済み（指紋 <code>{settings.publicKeyFingerprint}</code>）</span>
            <button className="btn-secondary" onClick={() => setEditKey(true)}>鍵を変える</button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: '0.85rem', color: '#4b5563', margin: '0.25rem 0 0.5rem' }}>
              サーバーには<b>公開鍵</b>だけを登録します。ファイルを開くための<b>秘密鍵</b>はサーバーに置かないので、
              サーバーが乗っ取られても書き出したファイルは読まれません。
              秘密鍵を無くすと、書き出したファイルは誰にも開けなくなります。
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <button className="btn-secondary" disabled={busy} onClick={gen}>このブラウザで鍵を作る</button>
              {settings?.publicKeyPem && (
                <button className="btn-secondary" onClick={() => { setPem(settings.publicKeyPem); setGenerated(null); setEditKey(false); }}>
                  変えずに戻る
                </button>
              )}
            </div>
            {generated && (
              <div style={box('#fffbeb', '#92400e')} data-private-key>
                <div>秘密鍵を作りました。<b>先にダウンロードして、USBメモリなどサーバーの外に保管してください。</b>
                  この画面を閉じると二度と取り出せません。</div>
                <button className="btn-primary" style={{ marginTop: '0.4rem' }} onClick={downloadPrivate}>
                  {generated.downloaded ? '秘密鍵をもう一度ダウンロード' : '秘密鍵をダウンロード'}
                </button>
              </div>
            )}
            <textarea value={pem} onChange={e => { setPem(e.target.value); }} rows={5}
                      aria-label="公開鍵"
                      placeholder={'-----BEGIN PUBLIC KEY-----\n…\n-----END PUBLIC KEY-----'}
                      style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.78rem', boxSizing: 'border-box' }} />
            {settings?.publicKeyPem && keyChanged && (
              <div style={box('#fffbeb', '#92400e')}>
                鍵を変えても、これまでに書き出したファイルは前の秘密鍵で開きます。前の秘密鍵も捨てないでください。
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
        {mustDownload && <span style={{ fontSize: '0.85rem', color: '#b45309' }}>先に秘密鍵をダウンロードしてください</span>}
        <button className="btn-primary" disabled={busy || mustDownload} onClick={save}>設定を保存</button>
      </div>
    </div>
  );
}

// ── ページ本体 ────────────────────────────────────────────────────
export default function DataRetention() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);     // { action, target, plan, token }
  const [confirmErr, setConfirmErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [sums, setSums] = useState({});             // closureId → rows

  const load = useCallback(async () => {
    try {
      const d = await api('/scopes');
      setData(d);
      setSettings(d.settings);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => (data?.closures || []).filter(c => ['closed', 'exported', 'purged'].includes(c.status)), [data]);
  const closureOf = (year, termId) => active.find(c => c.academic_year === year && (c.term_id ?? null) === (termId ?? null));

  // 年度ごとにまとめる
  const years = useMemo(() => {
    const m = new Map();
    for (const s of data?.scopes || []) {
      if (!m.has(s.academic_year)) m.set(s.academic_year, []);
      m.get(s.academic_year).push(s);
    }
    // 消去済みで授業が無くなった範囲も出す
    for (const c of active) {
      if (!m.has(c.academic_year)) m.set(c.academic_year, []);
      if (c.term_id && !m.get(c.academic_year).some(s => s.term_id === c.term_id)) {
        m.get(c.academic_year).push({ academic_year: c.academic_year, term_id: c.term_id, term_name: c.term_name, classes: 0, open_classes: 0, students: 0 });
      }
    }
    return [...m.entries()].sort((a, b) => b[0] - a[0]);
  }, [data, active]);

  const openPreview = async (action, target) => {
    setErr(''); setMsg(''); setConfirmErr(''); setBusy(true);
    try {
      const d = action === 'close'
        ? await api('/close/preview', { method: 'POST', body: JSON.stringify({ academicYear: target.year, termId: target.termId }) })
        : await api(`/closures/${target.id}/${action}/preview`, { method: 'POST', body: '{}' });
      setConfirm({ action, target, plan: d.plan, token: d.token, key: Date.now() });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const run = async (phrase) => {
    const { action, target, token: tk } = confirm;
    setBusy(true); setConfirmErr('');
    try {
      const d = action === 'close'
        ? await api('/close/execute', { method: 'POST', body: JSON.stringify({ academicYear: target.year, termId: target.termId, token: tk, phrase }) })
        : await api(`/closures/${target.id}/${action}/execute`, { method: 'POST', body: JSON.stringify({ token: tk, phrase }) });
      setConfirm(null);
      setMsg(d.message);
      await load();
    } catch (e) { setConfirmErr(e.message); }
    finally { setBusy(false); }
  };

  const doExport = async (c) => {
    setErr(''); setMsg(''); setBusy(true);
    try {
      const d = await api(`/closures/${c.id}/export`, { method: 'POST', body: '{}' });
      setMsg(`${c.label}を暗号化ファイルに書き出しました（${fmtBytes(d.bytes)}）。ダウンロードして、サーバーの外にも保管してください`);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const download = async (c) => {
    setErr('');
    try {
      const r = await fetch(`/api/retention/closures/${c.id}/download`, { headers: { Authorization: 'Bearer ' + token() } });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.message || 'ダウンロードに失敗しました');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = c.export_file || `archive-${c.id}.nkarc`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setErr(e.message); }
  };

  const toggleSummaries = async (c) => {
    if (sums[c.id]) { setSums(s => { const n = { ...s }; delete n[c.id]; return n; }); return; }
    try {
      const d = await api(`/closures/${c.id}/summaries`);
      setSums(s => ({ ...s, [c.id]: d.summaries }));
    } catch (e) { setErr(e.message); }
  };

  const t = today();
  const due = (c) => {
    const out = [];
    if (['closed', 'exported'].includes(c.status) && c.purge_after && day(c.purge_after) <= t) out.push('消去の時期です');
    if (c.fileExists && c.destroy_after && day(c.destroy_after) <= t) out.push('廃棄の時期です');
    return out;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
          <Link to="/academic">← 教務管理</Link>
        </div>
        <h1>年度の締め・保管</h1>
        <p>終わった年度（学期）の名簿・出席を読み取り専用にし、保存期間が過ぎたら暗号化して保管・消去します。</p>
      </div>

      {err && <div className="alert alert-error" style={box('#fef2f2', '#b91c1c')}>{err}</div>}
      {msg && <div className="alert alert-success" style={box('#f0fdf4', '#166534')}>{msg}</div>}

      <div className="card" style={{ fontSize: '0.88rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <Step n="1" text="学期中" />→<Step n="2" text="締める（読み取り専用）" />→
          <Step n="3" text="暗号化して書き出す → 消去" />→<Step n="4" text="保存期間後に廃棄" />
        </div>
        <p style={{ margin: '0.5rem 0 0', color: '#6b7280' }}>
          どの操作も自動では行いません。締める・再開・消去・廃棄は2回確認してから実行します。
          サーバーのバックアップ（pg_dump など）には消去前のデータが残るので、バックアップの保存期間も合わせてください。
        </p>
      </div>

      <SettingsCard settings={settings} onSaved={(s) => { setSettings(s); load(); }} setMsg={setMsg} setErr={setErr} />

      <div className="card">
        <div className="card-header"><h2>年度・学期</h2></div>
        {!data && <p>読み込み中…</p>}
        {data && years.length === 0 && <p>年度が設定された授業がありません。</p>}
        {years.map(([year, scopes]) => {
          const whole = closureOf(year, null);
          return (
            <div key={year} style={{ marginBottom: '1rem' }} data-year={year}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
                            borderBottom: '1px solid #e5e7eb', padding: '0.4rem 0' }}>
                <h3 style={{ margin: 0 }}>{year}年度</h3>
                {whole ? <Badge status={whole.status} /> : (
                  <button className="btn-secondary" disabled={busy}
                          onClick={() => openPreview('close', { year, termId: null })}>
                    年度全体を締める
                  </button>
                )}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                    <th style={th}>学期</th><th style={th}>授業</th><th style={th}>学生</th><th style={th}>状態</th><th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {scopes.map(s => {
                    const c = closureOf(year, s.term_id) || whole;
                    const closedHere = s.classes > 0 && s.open_classes === 0;
                    return (
                      <tr key={String(s.term_id)} style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td style={td}>{s.term_name || '学期なし'}</td>
                        <td style={td}>{s.classes}{s.held > 0 && <span style={{ color: '#b45309' }}>（保留 {s.held}）</span>}</td>
                        <td style={td}>{s.students}</td>
                        <td style={td}>{c ? <Badge status={c.status} /> : closedHere ? <Badge status="closed" /> : <span style={{ color: '#6b7280' }}>使用中</span>}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {!c && s.term_id && s.open_classes > 0 && (
                            <button className="btn-secondary" disabled={busy}
                                    onClick={() => openPreview('close', { year, termId: s.term_id })}>
                              締める
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div className="card-header"><h2>締めの記録</h2></div>
        {data && (data.closures || []).length === 0 && <p>まだ締めた年度はありません。</p>}
        {(data?.closures || []).map(c => (
          <div key={c.id} data-closure={c.id}
               style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.6rem 0.8rem', marginBottom: '0.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <b>{c.label}</b>
              <Badge status={c.status} />
              {due(c).map(x => <span key={x} style={{ fontSize: '0.78rem', color: '#b91c1c', fontWeight: 600 }}>● {x}</span>)}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: '0.3rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <span>締め {day(c.closed_at)}{c.closed_by_name ? `（${c.closed_by_name}）` : ''}</span>
              <span>授業 {c.class_count} / 学生 {c.student_count} / 出席 {c.attendance_count}</span>
              {['closed', 'exported'].includes(c.status) && <span>消去の目安 {day(c.purge_after)}</span>}
              {c.exported_at && <span>書き出し {day(c.exported_at)}{c.export_bytes ? `（${fmtBytes(c.export_bytes)}）` : ''}</span>}
              {c.destroy_after && c.fileExists && <span>廃棄の目安 {day(c.destroy_after)}</span>}
              {c.purged_at && <span>消去 {day(c.purged_at)}</span>}
              {c.destroyed_at && <span>ファイル廃棄 {day(c.destroyed_at)}</span>}
              {c.reopened_at && <span>再開 {day(c.reopened_at)}</span>}
              {c.key_fingerprint && c.fileExists && <span>鍵 <code>{c.key_fingerprint}</code></span>}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              {['closed', 'exported'].includes(c.status) && (
                <button className="btn-secondary" disabled={busy || !settings?.publicKeyFingerprint}
                        title={settings?.publicKeyFingerprint ? '' : '先に暗号化用の鍵を登録してください'}
                        onClick={() => doExport(c)}>
                  {c.status === 'exported' ? '書き出し直す' : '暗号化して書き出す'}
                </button>
              )}
              {c.fileExists && <button className="btn-secondary" onClick={() => download(c)}>ファイルをダウンロード</button>}
              {['closed', 'exported'].includes(c.status) && (
                <button className="btn-secondary" disabled={busy} onClick={() => openPreview('reopen', c)}>再開…</button>
              )}
              {c.status === 'exported' && (
                <button className="btn-danger" disabled={busy} onClick={() => openPreview('purge', c)}>データベースから消去…</button>
              )}
              {c.fileExists && (
                <button className="btn-danger" disabled={busy} onClick={() => openPreview('destroy', c)}>ファイルを廃棄…</button>
              )}
              {c.status === 'purged' && (
                <button className="btn-link" onClick={() => toggleSummaries(c)}>{sums[c.id] ? '集計を閉じる' : '残した集計を見る'}</button>
              )}
            </div>
            {sums[c.id] && (
              <table style={{ width: '100%', fontSize: '0.82rem', marginTop: '0.5rem', borderCollapse: 'collapse' }}>
                <thead><tr style={{ color: '#6b7280', textAlign: 'left' }}>
                  <th style={th}>授業</th><th style={th}>担当</th><th style={th}>履修</th><th style={th}>回</th><th style={th}>平均出席率</th>
                </tr></thead>
                <tbody>
                  {sums[c.id].map(s => (
                    <tr key={s.id}><td style={td}>{s.class_name}</td><td style={td}>{s.teacher_name || '—'}</td>
                      <td style={td}>{s.enrolled}</td><td style={td}>{s.held_sessions}</td>
                      <td style={td}>{s.avg_rate == null ? '—' : `${s.avg_rate}%`}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
        <details style={{ fontSize: '0.85rem', color: '#4b5563' }}>
          <summary style={{ cursor: 'pointer' }}>暗号化ファイルの開き方</summary>
          <p>秘密鍵のあるパソコン（Node.js が入っているもの）で、リポジトリの <code>backend/scripts/archive-decrypt.js</code> を使います。</p>
          <pre style={{ background: '#f3f4f6', padding: '0.5rem', borderRadius: 6, overflowX: 'auto' }}>
node archive-decrypt.js --key 秘密鍵.pem --in ファイル.nkarc --out 出力フォルダ</pre>
          <p>授業ごとの名簿と出席の CSV（Excel で開ける形）と、全データの JSON が出ます。</p>
        </details>
      </div>

      {confirm && (
        <ConfirmSteps key={confirm.key} action={confirm.action} plan={confirm.plan} busy={busy} error={confirmErr}
                      onRun={run}
                      onCancel={() => { setConfirm(null); setConfirmErr(''); }}
                      onReload={() => openPreview(confirm.action, confirm.target)} />
      )}
    </div>
  );
}

function Step({ n, text }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
      <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#e0e7ff', color: '#3730a3',
                     display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>{n}</span>
      {text}
    </span>
  );
}

const th = { padding: '0.35rem 0.4rem', fontWeight: 500, fontSize: '0.8rem' };
const td = { padding: '0.4rem' };
